import { createContext, useContext, useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Wrench, Phone, Mail, Palmtree } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const ukDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

const ClosedPage = ({ s }) => (
  <div className="min-h-screen bg-[#0B2545] text-white flex items-center px-6" data-testid="site-closed-page">
    <div className="max-w-2xl mx-auto py-20">
      <div className="flex items-center gap-3 mb-10">
        <span className="w-10 h-10 bg-white/10 rounded-sm flex items-center justify-center"><Wrench className="w-5 h-5" /></span>
        <span className="font-display font-black tracking-tight text-lg">HYDRONIC.<span className="block text-[10px] font-medium tracking-[0.2em] text-white/60">HEATING CONTROLS &amp; VALVES</span></span>
      </div>
      <p className="text-overline text-sky-300 mb-3">Temporarily closed</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tighter">We're updating our website</h1>
      <p className="mt-6 text-lg text-white/80" data-testid="site-closed-message">{s.message}</p>
      {s.reopen_date && (
        <p className="mt-4 text-base font-semibold text-sky-200" data-testid="site-closed-reopen">We'll be back on {ukDate(s.reopen_date)}.</p>
      )}
      <div className="mt-10 pt-8 border-t border-white/15 space-y-3 text-sm">
        <p className="text-white/60">Need help in the meantime?</p>
        <a href="tel:07443173458" className="flex items-center gap-2 hover:text-sky-300 transition-colors" data-testid="site-closed-phone"><Phone className="w-4 h-4" /> 07443 173458</a>
        <a href="mailto:enquiries@hhcv.co.uk" className="flex items-center gap-2 hover:text-sky-300 transition-colors" data-testid="site-closed-email"><Mail className="w-4 h-4" /> enquiries@hhcv.co.uk</a>
      </div>
    </div>
  </div>
);

const SiteStatus = createContext({ paused: false });
export const useOrdersPaused = () => useContext(SiteStatus).paused;

const HolidayBanner = ({ s }) => (
  <div className="bg-[#0B2545] text-white text-sm text-center py-2.5 px-4 flex items-center justify-center gap-2" data-testid="holiday-banner">
    <Palmtree className="w-4 h-4 text-sky-300 shrink-0" />
    <span><strong>Orders are paused</strong>{s.reopen_date ? ` — we'll be back on ${ukDate(s.reopen_date)}` : " for a short while"}. You're welcome to browse in the meantime.</span>
  </div>
);

export const SiteClosedGate = ({ children }) => {
  const [s, setS] = useState(null);
  const { user } = useAuth();
  const { pathname } = useLocation();

  useEffect(() => { api.get("/settings/closed").then((r) => setS(r.data)).catch(() => {}); }, [pathname]);

  if (!s?.active) return children;
  if (s.mode === "holiday") {
    return <SiteStatus.Provider value={{ paused: true }}><HolidayBanner s={s} />{children}</SiteStatus.Provider>;
  }
  if (user?.role === "admin") {
    return (
      <>
        <div className="bg-amber-500 text-black text-sm font-semibold text-center py-2 px-4" data-testid="site-closed-admin-bar">
          Site is closed to customers{s.reopen_date ? ` until ${ukDate(s.reopen_date)}` : ""}. You're seeing it as admin. <Link to="/account" className="underline">Change in your dashboard</Link>
        </div>
        {children}
      </>
    );
  }
  if (pathname === "/account") return children;
  return <ClosedPage s={s} />;
};
